import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { uniq } from 'lodash';

const autosaveTimeout = 1000;

// Caps how many autosave requests are in flight at once. A mass-edit on 30+ orders used to mark
// them all dirty within the same debounce window and fire all 30 PUT /api/Order/autosave/:id
// calls simultaneously - each one pins a Lambda invocation (and, via that, a Postgres connection)
// for its duration, so an unbounded burst like that was a direct contributor to Lambda/Postgres
// connection exhaustion. Processing them with a small worker pool instead keeps total save time
// roughly the same (still network-bound) while bounding peak concurrency from a single browser tab.
const MAX_CONCURRENT_AUTOSAVES = 10;

// Runs saveOne(order) over orders using at most MAX_CONCURRENT_AUTOSAVES workers in parallel,
// each worker picking up the next order as soon as it finishes its current one. A failure saving
// one order is logged and skipped rather than aborting the whole worker, so one bad order doesn't
// prevent the rest of the batch from being saved.
const saveOrdersWithBoundedConcurrency = async (orders, saveOne) => {
    let nextIndex = 0;
    const worker = async () => {
        while (nextIndex < orders.length) {
            const order = orders[nextIndex++];
            try {
                await saveOne(order);
            } catch (error) {
                console.error('Autosave failed:', error);
            }
        }
    };
    const workerCount = Math.min(MAX_CONCURRENT_AUTOSAVES, orders.length);
    await Promise.all(Array.from({ length: workerCount }, worker));
};

// Shared debounced-autosave logic for the sales and customer-portal shopping baskets. Both containers
// used to carry an identical copy of this (down to the bugs); this hook is parameterized with the
// redux pieces that actually differ between the two (state slice + action creators) so there is a
// single place to fix/extend the autosave behavior going forward.
//
// selectOrderState: selector returning the orderdata/customerorderdata state slice
//                    ({ pendingOrders, sendingOrdersInProgress, orderSendFailed, savingCombinedOrder,
//                       autosaving, autosavingFailed })
// actions: { removeOrder, sendOrders, sendOrdersCombined, saveOrderToStore, autosaveOrder, getLocationFull }
// retryTimeout: delay before retrying a failed autosave (ms)
export default function useOrderAutosave({ selectOrderState, actions, retryTimeout = 5000 }) {
    const dispatch = useDispatch();
    const {
        pendingOrders,
        sendingOrdersInProgress,
        orderSendFailed,
        savingCombinedOrder,
        autosaving,
        autosavingFailed,
    } = useSelector(selectOrderState);

    const autosavingRef = useRef(false);
    const sendingOrdersInProgressRef = useRef(false);
    const pendingOrdersRef = useRef([]);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const dirtyOrders = useRef([]);
    const timerHandle = useRef(null);

    useEffect(() => {
        autosavingRef.current = Object.keys(autosaving).length > 0;
    }, [autosaving]);

    useEffect(() => {
        pendingOrdersRef.current = pendingOrders;
    }, [pendingOrders]);

    useEffect(() => {
        sendingOrdersInProgressRef.current = sendingOrdersInProgress;
    }, [sendingOrdersInProgress]);

    const saveDirtyOrders = useCallback(() => {
        try {
            if (dirtyOrders.current.length === 0) return;

            const ordersToSave = uniq(dirtyOrders.current)
                .map((id) => pendingOrdersRef.current.find((o) => o.id === id))
                // order may have been removed from the basket since being marked dirty - skip it,
                // otherwise a single removed order aborts autosaving the rest of the batch
                .filter((o) => o != null);
            dirtyOrders.current = [];
            timerHandle.current = null;

            // dispatch (not a direct call) so redux-thunk supplies getState - autosaveOrder needs it
            // to read back the latest store state when reconciling the server's response. Not
            // awaited here - saving proceeds in the background with bounded concurrency while the
            // rest of the UI keeps responding, same as the previous unbounded fire-and-forget loop.
            saveOrdersWithBoundedConcurrency(ordersToSave, (o) => dispatch(actions.autosaveOrder(o))).catch((error) =>
                console.error('Autosave failed:', error)
            );
            setUnsavedChanges(false);
        } catch (error) {
            console.error('Autosave failed:', error);
        }
    }, [dispatch, actions]);

    const doAutosave = useCallback(() => {
        if (!autosavingRef.current && !sendingOrdersInProgressRef.current) {
            saveDirtyOrders();
        } else if (!sendingOrdersInProgressRef.current) {
            timerHandle.current = setTimeout(doAutosave, autosaveTimeout);
        }
    }, [saveDirtyOrders]);

    useEffect(() => {
        return () => {
            if (timerHandle.current) {
                saveDirtyOrders();
                clearTimeout(timerHandle.current);
            }
        };
    }, [saveDirtyOrders]);

    useEffect(() => {
        if (Object.keys(autosavingFailed).length) {
            for (const id of Object.keys(autosavingFailed)) {
                dirtyOrders.current.push(parseInt(id));
            }
            if (!timerHandle.current) timerHandle.current = setTimeout(doAutosave, retryTimeout);
        }
    }, [autosavingFailed, doAutosave, retryTimeout]);

    const sanitizeOrder = (order) => {
        if (order.orderedFilters != null && order.orderedFilters.length > 0) {
            for (let fo of order.orderedFilters) {
                fo.count = parseInt(fo.count);
            }
        }
    };

    const orderChanged = useCallback(
        (order) => {
            order.updateTotalPrice();
            sanitizeOrder(order);
            dispatch(actions.saveOrderToStore(order));
            setUnsavedChanges(true);
            dirtyOrders.current.push(order.id);
            if (!timerHandle.current) {
                timerHandle.current = setTimeout(doAutosave, autosaveTimeout);
            }
        },
        [dispatch, actions, doAutosave]
    );

    const removeOrder = useCallback((id) => dispatch(actions.removeOrder(id)), [dispatch, actions]);
    const sendOrders = useCallback(
        (orders, options, language) => dispatch(actions.sendOrders(orders, options, language)),
        [dispatch, actions]
    );
    const sendOrdersCombined = useCallback(
        (orders, options, language) => dispatch(actions.sendOrdersCombined(orders, options, language)),
        [dispatch, actions]
    );
    const getLocationFull = useCallback((id) => dispatch(actions.getLocationFull(id)), [dispatch, actions]);

    return {
        pendingOrders,
        sendingOrdersInProgress,
        orderSendFailed,
        savingCombinedOrder,
        autosaving: Object.keys(autosaving).length > 0,
        autosavingFailed: Object.keys(autosavingFailed).length > 0,
        unsavedChanges,
        orderChanged,
        removeOrder,
        sendOrders,
        sendOrdersCombined,
        getLocationFull,
    };
}
